import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Mail, CheckCircle2, Loader2, Info,
  Send, Users, Briefcase, CreditCard, UserCheck, Bell,
  FileText, Clock, AlertTriangle, Star, Shield
} from "lucide-react";
import { toast } from "sonner";

const AdminEmailTests = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');
  }, [navigate]);

  // Complete list of ALL email templates in the system
  const emailTypes = [
    // ===== JOB NOTIFICATIONS =====
    {
      id: "admin_job_posted_notification",
      name: "Admin: New Job Posted",
      description: "Sent to admin when a new job is posted and needs approval",
      category: "Admin Notifications",
      icon: Bell,
      color: "red"
    },
    {
      id: "new_job_notification_simple",
      name: "New Job Alert (Simple)",
      description: "Modern detailed template sent to nearby crew when a simple job is approved",
      category: "Job Notifications",
      icon: Briefcase,
      color: "purple"
    },
    {
      id: "new_job_notification_advanced",
      name: "New Job Alert (Advanced)",
      description: "Modern detailed template with all crew positions/roles displayed",
      category: "Job Notifications",
      icon: Briefcase,
      color: "purple"
    },

    // ===== APPLICATIONS =====
    {
      id: "application_confirmation",
      name: "Application Confirmation",
      description: "Sent to applicant confirming their job application was received",
      category: "Applications",
      icon: Send,
      color: "green"
    },
    {
      id: "application_notification",
      name: "Application Received (Single)",
      description: "Sent to job owner when a single application is received",
      category: "Applications",
      icon: Users,
      color: "blue"
    },
    {
      id: "batched_applicant_notification",
      name: "Applications Summary (Batched)",
      description: "Sent to job owner with all new applicants in a 10-minute batch",
      category: "Applications",
      icon: Users,
      color: "blue"
    },

    // ===== JOB OFFERS =====
    {
      id: "job_offer",
      name: "Job Offer Received",
      description: "Sent to crew member when they receive a job offer (48hr expiry)",
      category: "Job Offers",
      icon: Star,
      color: "yellow"
    },
    {
      id: "offer_reminder",
      name: "Offer Reminder (24hr)",
      description: "Reminder sent when offer expires in 24 hours",
      category: "Job Offers",
      icon: Clock,
      color: "orange"
    },
    {
      id: "offer_expired",
      name: "Offer Expired",
      description: "Sent to crew when their job offer has expired",
      category: "Job Offers",
      icon: AlertTriangle,
      color: "red"
    },
    {
      id: "job_confirmed_owner",
      name: "Job Confirmed (to Owner)",
      description: "Sent to job owner when crew accepts their offer",
      category: "Job Offers",
      icon: CheckCircle2,
      color: "green"
    },
    {
      id: "job_confirmed_crew",
      name: "Job Confirmed (to Crew)",
      description: "Sent to crew member confirming they accepted the job",
      category: "Job Offers",
      icon: CheckCircle2,
      color: "green"
    },
    {
      id: "job_declined",
      name: "Offer Declined",
      description: "Sent to job owner when crew declines their offer",
      category: "Job Offers",
      icon: AlertTriangle,
      color: "red"
    },

    // ===== JOB MANAGEMENT =====
    {
      id: "how_did_it_go",
      name: "How Did It Go?",
      description: "Sent to job owner 3 days after job asking for completion",
      category: "Job Management",
      icon: Star,
      color: "orange"
    },
    {
      id: "complete_reminder",
      name: "Complete Job Reminder",
      description: "Final reminder 5 days after job before auto-completion",
      category: "Job Management",
      icon: AlertTriangle,
      color: "red"
    },

    // ===== PAYMENTS =====
    {
      id: "upcoming_payment",
      name: "Upcoming Payment Notice",
      description: "Sent to job owner 2 days before payment is charged",
      category: "Payments",
      icon: CreditCard,
      color: "blue"
    },
    {
      id: "payment_success",
      name: "Payment Successful",
      description: "Confirmation sent when payment is successfully processed",
      category: "Payments",
      icon: CheckCircle2,
      color: "green"
    },
    {
      id: "payment_released",
      name: "Payment Released (to Crew)",
      description: "Sent to crew when their payment has been released",
      category: "Payments",
      icon: CreditCard,
      color: "green"
    },
    {
      id: "release_payment_admin",
      name: "Release Payment (Admin)",
      description: "Notification to admin that a payment is ready for release",
      category: "Payments",
      icon: Bell,
      color: "purple"
    },
    {
      id: "invoice_email",
      name: "Invoice Generated",
      description: "Invoice email sent after job completion",
      category: "Payments",
      icon: FileText,
      color: "blue"
    },

    // ===== USER MANAGEMENT =====
    {
      id: "profile_pending",
      name: "Profile Pending (Admin)",
      description: "Notification to admin when new user profile needs approval",
      category: "User Management",
      icon: Bell,
      color: "orange"
    },
    {
      id: "profile_approved",
      name: "Profile Approved",
      description: "Sent to user when their profile is approved by admin",
      category: "User Management",
      icon: UserCheck,
      color: "green"
    },

    // ===== LEADS =====
    {
      id: "lead_submission_confirmation",
      name: "Lead Submission Confirmation",
      description: "Sent to lead poster confirming their request was received",
      category: "Leads",
      icon: Send,
      color: "teal"
    },
    {
      id: "lead_approval_notification",
      name: "Lead Available (to Users)",
      description: "Sent to matching users when a new lead is approved",
      category: "Leads",
      icon: Bell,
      color: "teal"
    },
    {
      id: "lead_reveal_email",
      name: "Lead Details Revealed",
      description: "Sent to user when they purchase/reveal lead contact details",
      category: "Leads",
      icon: FileText,
      color: "teal"
    },
    {
      id: "you_got_a_match_email",
      name: "You Got a Match!",
      description: "Sent to lead poster when a crew member reveals their lead",
      category: "Leads",
      icon: Star,
      color: "teal"
    }
  ];

  const handleSendTestEmail = async (emailType) => {
    setSending(prev => ({ ...prev, [emailType]: true }));

    try {
      const token = localStorage.getItem('shootzu_admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/test-email/${emailType}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data.status === "success") {
        toast.success(`Test email sent successfully to hello@shootzu.com`);
        setSent(prev => ({ ...prev, [emailType]: true }));

        // Reset sent state after 3 seconds
        setTimeout(() => {
          setSent(prev => ({ ...prev, [emailType]: false }));
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(`Failed to send test email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSending(prev => ({ ...prev, [emailType]: false }));
    }
  };

  // Group emails by category
  const groupedEmails = emailTypes.reduce((acc, email) => {
    if (!acc[email.category]) {
      acc[email.category] = [];
    }
    acc[email.category].push(email);
    return acc;
  }, {});

  // Category order
  const categoryOrder = [
    "Admin Notifications",
    "Job Notifications",
    "Applications",
    "Job Offers",
    "Job Management",
    "Payments",
    "User Management",
    "Leads"
  ];

  // Per-category chip (icon tile) styling + section header icon
  const categoryMeta = {
    "Admin Notifications": { chipBg: "#EDE4F4", chipFg: "#78287C", icon: Shield },
    "Job Notifications":   { chipBg: "#DBEAFE", chipFg: "#2563EB", icon: Briefcase },
    "Applications":        { chipBg: "#CCFBF1", chipFg: "#0F766E", icon: FileText },
    "Job Offers":          { chipBg: "#FEF3C7", chipFg: "#B45309", icon: Star },
    "Job Management":      { chipBg: "#E0E7FF", chipFg: "#4338CA", icon: Briefcase },
    "Payments":            { chipBg: "#DCFCE7", chipFg: "#15803D", icon: CreditCard },
    "User Management":     { chipBg: "#FEE2E2", chipFg: "#B91C1C", icon: Users },
    "Leads":               { chipBg: "#CFFAFE", chipFg: "#0E7490", icon: Bell }
  };

  const totalTemplates = emailTypes.length;

  return (
    <div className="min-h-full" style={{ background: '#F5F6F9' }}>
      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div className="flex items-center gap-[11px]">
          <h1 className="sz-h1" style={{ margin: 0 }}>Email Tests</h1>
          <span
            style={{
              fontFamily: 'Poppins', fontSize: '11.5px', fontWeight: 700,
              color: '#2563EB', background: '#DBEAFE', padding: '4px 10px', borderRadius: '999px'
            }}
          >
            {totalTemplates} templates
          </span>
        </div>
      </header>

      <main style={{ padding: '22px 30px 56px', maxWidth: '1280px' }}>
        {/* Info banner */}
        <div
          className="flex items-start gap-[11px]"
          style={{
            padding: '13px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: '13px', marginBottom: '20px'
          }}
        >
          <Info className="w-[18px] h-[18px] shrink-0 mt-px" style={{ color: '#2563EB' }} />
          <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: '#1E40AF' }}>
            Send a live preview of any transactional template to <strong>hello@shootzu.com</strong> to check
            formatting and copy. These do not affect real users.
          </div>
        </div>

        {/* Email categories */}
        {categoryOrder.map((category) => {
          const emails = groupedEmails[category];
          if (!emails) return null;
          const meta = categoryMeta[category] || { chipBg: '#DBEAFE', chipFg: '#2563EB', icon: Mail };
          const SectionIcon = meta.icon;

          return (
            <div key={category} style={{ marginBottom: '26px' }}>
              <div className="flex items-center gap-[9px]" style={{ marginBottom: '13px' }}>
                <div
                  className="flex items-center justify-center"
                  style={{ width: '28px', height: '28px', borderRadius: '8px', background: meta.chipBg, color: meta.chipFg }}
                >
                  <SectionIcon className="w-[15px] h-[15px]" />
                </div>
                <h2 className="sz-h3" style={{ fontSize: '15px', margin: 0 }}>{category}</h2>
                <span style={{ fontSize: '11px', color: '#A8AEBC', fontFamily: 'Poppins' }}>
                  {emails.length} templates
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '12px'
                }}
              >
                {emails.map((email) => {
                  const IconComponent = email.icon || Mail;
                  const isSending = sending[email.id];
                  const isSent = sent[email.id];

                  return (
                    <div
                      key={email.id}
                      className="sz-card"
                      style={{ borderRadius: '14px', padding: '15px 16px', display: 'flex', flexDirection: 'column' }}
                    >
                      <div className="flex items-start gap-[11px]">
                        <div
                          className="flex items-center justify-center shrink-0"
                          style={{ width: '34px', height: '34px', borderRadius: '9px', background: meta.chipBg, color: meta.chipFg }}
                        >
                          <IconComponent className="w-[17px] h-[17px]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: '13px', fontWeight: 600 }}>{email.name}</div>
                          <div style={{ fontSize: '11.5px', lineHeight: 1.45, color: '#8A90A2', marginTop: '3px' }}>
                            {email.description}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleSendTestEmail(email.id)}
                        disabled={isSending}
                        className="flex items-center justify-center gap-[7px]"
                        style={{
                          marginTop: '13px', padding: '9px', borderRadius: '9px',
                          fontFamily: 'Work Sans', fontSize: '12px', fontWeight: 600,
                          cursor: isSending ? 'not-allowed' : 'pointer',
                          border: isSent ? '1px solid #BBF7D0' : '1px solid #E0E3EB',
                          background: isSent ? '#F0FDF4' : '#fff',
                          color: isSent ? '#15803D' : '#2B3142',
                          opacity: isSending ? 0.6 : 1
                        }}
                      >
                        {isSending ? (
                          <>
                            <Loader2 className="w-[14px] h-[14px] animate-spin" />
                            Sending...
                          </>
                        ) : isSent ? (
                          <>
                            <CheckCircle2 className="w-[14px] h-[14px]" />
                            Sent!
                          </>
                        ) : (
                          <>
                            <Send className="w-[14px] h-[14px]" />
                            Send Test
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};

export default AdminEmailTests;
